import { db } from '../firebase';
import { collection, addDoc, getDocs, setDoc, doc, query, where, updateDoc } from 'firebase/firestore';

// Utility function to create user documents for existing users
// This should be run once to populate the users collection with existing users
export const migrateExistingUsers = async () => {
  try {
    console.log('Starting user migration...');
    
    // Get all unique user IDs from acceptedEvents and pendingEvents
    const userIds = new Set();
    
    // Get user IDs from acceptedEvents
    const acceptedEventsSnapshot = await getDocs(collection(db, 'acceptedEvents'));
    acceptedEventsSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.userId) {
        userIds.add(data.userId);
      }
    });
    
    // Get user IDs from pendingEvents
    const pendingEventsSnapshot = await getDocs(collection(db, 'pendingEvents'));
    pendingEventsSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.userId) {
        userIds.add(data.userId);
      }
    });
    
    console.log(`Found ${userIds.size} unique user IDs in events`);
    
    // Check which users already exist in the users collection
    const existingUsersSnapshot = await getDocs(collection(db, 'users'));
    const existingUserIds = new Set();
    existingUsersSnapshot.forEach(doc => {
      existingUserIds.add(doc.data().uid);
    });
    
    console.log(`Found ${existingUserIds.size} existing users in users collection`);
    
    // Create user documents for users that don't exist yet
    const usersToCreate = Array.from(userIds).filter(uid => !existingUserIds.has(uid));
    
    console.log(`Need to create ${usersToCreate.length} user documents`);
    
    for (const uid of usersToCreate) {
      try {
        // Create a basic user document with minimal information
        // Note: We don't have access to email from events, so we'll set it as null
        // Users will need to log in once for their email to be updated
        const userDocRef = doc(db, 'users', uid);
        await setDoc(userDocRef, {
          uid: uid,
          email: null, // Will be updated when user logs in
          displayName: null,
          photoURL: null,
          createdAt: new Date().toISOString(),
          lastLoginAt: null,
          isActive: true,
          migrated: true // Flag to indicate this was migrated
        });
        
        console.log(`Created user document for UID: ${uid}`);
      } catch (error) {
        console.error(`Error creating user document for ${uid}:`, error);
      }
    }
    
    console.log('User migration completed!');
    return {
      success: true,
      totalUsers: userIds.size,
      existingUsers: existingUserIds.size,
      createdUsers: usersToCreate.length
    };
    
  } catch (error) {
    console.error('Error during user migration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Function to update migrated user emails when they log in
export const updateMigratedUserEmail = async (uid, email) => {
  try {
    const userDocRef = doc(db, 'users', uid);
    const userQuery = query(collection(db, 'users'), where('uid', '==', uid));
    const userSnapshot = await getDocs(userQuery);
    
    if (!userSnapshot.empty) {
      const userData = userSnapshot.docs[0].data();
      if (userData.migrated && !userData.email) {
        await updateDoc(userDocRef, {
          email: email,
          lastLoginAt: new Date().toISOString()
        });
        console.log(`Updated email for migrated user: ${uid}`);
      }
    }
  } catch (error) {
    console.error('Error updating migrated user email:', error);
  }
};